import React from 'react';
import { Link } from 'react-router-dom';
import { FooterSection } from '../components/landing';
import { usePageTitle } from '../hooks/usePageTitle';

const RefundPolicyPage: React.FC = () => {
  usePageTitle('Refund Policy');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-semibold mb-6 backdrop-blur-sm border border-white/20">
              Legal
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Refund Policy
            </h1>
            <p className="text-xl text-gray-300">
              Please review our refund policy before purchasing a subscription.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 11, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-gray-600">

            {/* 1. Overview */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Overview</h2>
              <p>
                This Refund Policy applies to all paid subscription plans purchased through
                the Retrosive platform at <strong>retrosive.com</strong>.
              </p>
              <p className="mt-4">
                We offer a <strong>free plan</strong> that allows you to fully evaluate the
                Service before committing to a paid subscription. We strongly encourage all
                users to try the free plan first to ensure Retrosive meets their needs, as
                all payments for paid subscriptions are final and non-refundable.
              </p>
            </div>

            {/* 2. No Refund Policy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. No Refund Policy</h2>

              <div className="p-6 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-amber-900 font-semibold text-lg mb-3">
                  All payments are final and non-refundable.
                </p>
                <p className="text-amber-800">
                  Once a payment has been processed for a subscription plan, it cannot be
                  refunded, whether in whole or in part. This applies to all subscription
                  types, including monthly and annual plans. Even if you cancel your
                  subscription before the end of the current billing period, no refund
                  will be issued for the remaining unused time.
                </p>
              </div>

              <p className="mt-6">
                By subscribing to a paid plan, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>All charges are non-refundable</li>
                <li>No partial or prorated refunds will be issued upon cancellation</li>
                <li>Downgrading to a lower plan does not entitle you to a refund for the difference</li>
                <li>Unused time remaining on your subscription will not be refunded</li>
              </ul>
            </div>

            {/* 3. Free Trial */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Free Trial Period</h2>
              <p>
                Paid subscription plans may include a free trial period (typically 14 days).
                During the trial period, you can access all features of the selected plan at
                no charge. If you cancel before the trial period ends, you will not be charged.
              </p>
              <p className="mt-4">
                If you do not cancel before the trial period ends, your subscription will
                automatically convert to a paid subscription, and your payment method will
                be charged. Once charged, the payment is non-refundable as stated in this policy.
              </p>
              <p className="mt-4">
                It is your responsibility to cancel the trial before it ends if you do not
                wish to continue with a paid subscription.
              </p>
            </div>

            {/* 4. Cancellation */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription Cancellation</h2>
              <p>
                You may cancel your subscription at any time through your account settings
                on the{' '}
                <Link to="/app/subscription" className="text-orange-500 hover:text-orange-600 font-medium">
                  Subscription page
                </Link>.
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>
                  <strong>Monthly subscriptions:</strong> Your subscription remains active
                  until the end of the current monthly billing period. No refund will be
                  issued for the remaining days.
                </li>
                <li>
                  <strong>Annual subscriptions:</strong> Your subscription remains active
                  until the end of the current annual billing period. No refund will be
                  issued for the remaining months.
                </li>
              </ul>
              <p className="mt-4">
                After cancellation, no further charges will be made. Your account will be
                downgraded to the Free plan at the end of the current billing period. Your
                data will be retained but some features may be restricted based on Free plan
                limits.
              </p>
            </div>

            {/* 5. Plan Changes */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Plan Upgrades and Downgrades</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.1 Upgrades</h3>
              <p>
                When you upgrade to a higher-tier plan, the price difference will be prorated
                for the remainder of your current billing cycle. You will gain immediate access
                to the upgraded plan's features.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 Downgrades</h3>
              <p>
                When you downgrade to a lower-tier plan, the change will take effect at the
                beginning of your next billing cycle. You will continue to have access to your
                current plan's features until then. No refund or credit will be issued for
                the price difference.
              </p>
            </div>

            {/* 6. Exceptions */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Exceptions</h2>
              <p>
                In the following limited circumstances, we may, at our sole discretion,
                issue a refund or account credit:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>
                  <strong>Duplicate charges:</strong> If you were accidentally charged more
                  than once for the same subscription period due to a technical error.
                </li>
                <li>
                  <strong>Unauthorized charges:</strong> If a charge was made without your
                  authorization and you can demonstrate that the charge was fraudulent.
                </li>
              </ul>
              <p className="mt-4">
                These exceptions are handled on a case-by-case basis and are not guaranteed.
                To report such issues, please contact us at{' '}
                <a href="mailto:retrosivecom@gmail.com" className="text-orange-500 hover:text-orange-600 font-medium">
                  retrosivecom@gmail.com
                </a>{' '}
                as soon as possible.
              </p>
            </div>

            {/* 7. Payment Processing */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Payment Processing</h2>
              <p>
                All payments are processed through Stripe, our third-party payment processor.
                Retrosive does not store your credit card information directly.
              </p>
              <p className="mt-4">
                For questions about specific charges on your statement, please contact us
                before disputing the charge with your bank. Chargebacks initiated without
                first contacting us may result in account suspension.
              </p>
            </div>

            {/* 8. Changes */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Changes to This Policy</h2>
              <p>
                We reserve the right to modify this Refund Policy at any time. Changes will be
                posted on this page with an updated "Last updated" date. The policy in effect
                at the time of your purchase will apply to that transaction.
              </p>
            </div>

            {/* 9. Contact */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
              <p>
                If you have any questions about this Refund Policy, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-xl border border-gray-200">
                <p className="font-semibold text-gray-900">Retrosive</p>
                <p className="mt-2">
                  Email:{' '}
                  <a href="mailto:retrosivecom@gmail.com" className="text-orange-500 hover:text-orange-600 font-medium">
                    retrosivecom@gmail.com
                  </a>
                </p>
                <p className="mt-1">
                  Website:{' '}
                  <a href="https://retrosive.com" className="text-orange-500 hover:text-orange-600 font-medium">
                    retrosive.com
                  </a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default RefundPolicyPage;
